import React, { useState, useCallback, useEffect } from 'react';
import { GraphViewer } from '../components/graph/GraphViewer';
import { GraphFilters, type GraphFilterState } from '../components/graph/GraphFilters';
import { EvidencePanel } from '../components/graph/EvidencePanel';
import { EntityDetail } from '../components/graph/EntityDetail';
import {
  getGraphSlice, generateReport, listContentItems,
  type GraphEntity, type GraphRelation, type GraphSlice, type ContentItem,
} from '../services/api';
import { FileText, CheckSquare, Square } from 'lucide-react';

type PanelMode = 'none' | 'entity' | 'evidence';

const DEFAULT_FILTERS: GraphFilterState = {
  minStrength:   0.4,
  minSourceReliability: 0,
  levelFilter:   ['MEDIUM', 'STRONG'],
  onlyValidated: true,
  relationTypes: [],
  entityTypes:   [],
  sourceTypes:   [],
  channelTypes:  [],
  hopDepth:      2,
  timeRange:     null,
};

/** Convert a levelFilter whitelist to the minimum level value for the backend. */
function levelFilterToMinLevel(
  lf: ('WEAK' | 'MEDIUM' | 'STRONG')[]
): 'WEAK' | 'MEDIUM' | 'STRONG' | undefined {
  if (lf.length === 0) return undefined;
  if (lf.includes('WEAK'))   return 'WEAK';
  if (lf.includes('MEDIUM')) return 'MEDIUM';
  return 'STRONG';
}

const LEGEND_NODES: { label: string; color: string }[] = [
  { label: 'Persona',        color: '#3b82f6' },
  { label: 'Organizzazione', color: '#8b5cf6' },
  { label: 'Luogo',          color: '#10b981' },
  { label: 'Evento',         color: '#f59e0b' },
  { label: 'Concetto',       color: '#14b8a6' },
  { label: 'Valore',         color: '#6b7280' },
];

const LEGEND_LEVELS: { label: string; color: string; dash?: boolean }[] = [
  { label: 'STRONG',  color: '#4ade80' },
  { label: 'MEDIUM',  color: '#facc15' },
  { label: 'WEAK',    color: '#9ca3af', dash: true },
];

export function GraphPage() {
  const [query, setQuery]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [graphSlice, setGraphSlice] = useState<GraphSlice | null>(null);
  const [error, setError]           = useState<string | null>(null);

  const [filters, setFilters]         = useState<GraphFilterState>(DEFAULT_FILTERS);
  const [panelMode, setPanelMode]     = useState<PanelMode>('none');
  const [selectedEntity, setSelectedEntity]     = useState<GraphEntity | null>(null);
  const [selectedRelation, setSelectedRelation] = useState<GraphRelation | null>(null);

  const [reportLoading, setReportLoading] = useState(false);
  const [reportBody, setReportBody]       = useState<string | null>(null);
  const [groupByType, setGroupByType]     = useState(true);

  const [contentItems, setContentItems]     = useState<ContentItem[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);

  useEffect(() => {
    listContentItems()
      .then((items) => {
        const synced = items.filter((i) =>
          i.pipeline_status === 'GRAPH_SYNCED' || i.pipeline_status === 'VALIDATED'
        );
        setContentItems(synced);
        setSelectedDocIds(synced.map((i) => i.id));
      })
      .catch(() => {});
  }, []);

  const toggleDoc = (id: string) => {
    setSelectedDocIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setGraphSlice(null);
    setSelectedEntity(null);
    setSelectedRelation(null);
    setPanelMode('none');
    setReportBody(null);

    try {
      const slice = await getGraphSlice('query', { queryText: query }, {
        maxHops:        filters.hopDepth,
        minStrength:    filters.minStrength,
        minLevel:       levelFilterToMinLevel(filters.levelFilter),
        onlyValidated:  filters.onlyValidated,
        entityTypes:    filters.entityTypes.length > 0 ? filters.entityTypes : undefined,
        timeRange:      filters.timeRange ?? undefined,
        sourceTypes:    filters.sourceTypes.length > 0 ? filters.sourceTypes : undefined,
        channelTypes:   filters.channelTypes.length > 0 ? filters.channelTypes : undefined,
        contentItemIds: selectedDocIds.length > 0 ? selectedDocIds : undefined,
      });
      setGraphSlice(slice);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento del grafo');
    } finally {
      setLoading(false);
    }
  }, [query, filters, selectedDocIds]);

  const handleEntityClick = useCallback(async (entity: GraphEntity) => {
    setSelectedEntity(entity);
    setSelectedRelation(null);
    setPanelMode('entity');

    try {
      const slice = await getGraphSlice('entity', { entityKey: entity.entity_key }, {
        maxHops:        filters.hopDepth,
        minStrength:    filters.minStrength,
        onlyValidated:  filters.onlyValidated,
        contentItemIds: selectedDocIds.length > 0 ? selectedDocIds : undefined,
      });
      setGraphSlice((prev) => prev ? {
        ...prev,
        entities:  [...new Map([...(prev.entities ?? []), ...(slice.entities ?? [])].map((e) => [e.entity_key, e])).values()],
        relations: [...new Map([...(prev.relations ?? []), ...(slice.relations ?? [])].map((r) => [`${r.subject}|${r.type}|${r.object}`, r])).values()],
        snippets:  [...(prev.snippets ?? []), ...(slice.snippets ?? [])],
        documents: [...new Map([...(prev.documents ?? []), ...(slice.documents ?? [])].map((d) => [d.id, d])).values()],
      } : slice);
    } catch {
      // Non-critical: entity expansion failed silently
    }
  }, [filters, selectedDocIds]);

  const handleRelationClick = useCallback((relation: GraphRelation) => {
    setSelectedRelation(relation);
    setPanelMode('evidence');
  }, []);

  const handleGenerateReport = useCallback(async () => {
    if (!query.trim()) return;
    setReportLoading(true);
    setError(null);

    try {
      const report = await generateReport({
        focusType:  'query',
        focus:      { queryText: query },
        reportType: 'cross_document',
        language:   'it',
        save:       true,
      });
      setReportBody(report.body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nella generazione report');
    } finally {
      setReportLoading(false);
    }
  }, [query]);

  const availableRelationTypes = Array.from(
    new Set((graphSlice?.relations ?? []).map((r) => r.type))
  );
  const availableSourceTypes = Array.from(
    new Set((graphSlice?.documents ?? []).map((d) => d.source_type))
  );
  const availableChannelTypes = Array.from(
    new Set((graphSlice?.documents ?? []).map((d) => d.channel_type).filter((c): c is string => !!c))
  );

  return (
    <div className="flex flex-col h-full bg-gray-950 text-white relative">
      {/* Search bar */}
      <div className="flex items-center gap-3 p-4 bg-gray-900 border-b border-gray-800">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Cerca entità, eventi, relazioni…"
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors"
        >
          {loading ? 'Caricamento…' : 'Cerca'}
        </button>
        <button
          onClick={handleGenerateReport}
          disabled={reportLoading || !graphSlice || loading}
          className="px-4 py-2 bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors"
        >
          {reportLoading ? 'Generando…' : 'Genera report'}
        </button>
        <button
          onClick={() => setGroupByType((v) => !v)}
          title={groupByType ? 'Disabilita raggruppamento per tipo' : 'Abilita raggruppamento per tipo'}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
            groupByType
              ? 'bg-teal-700 border-teal-500 text-white'
              : 'bg-gray-800 border-gray-600 text-gray-400 hover:text-white'
          }`}
        >
          Raggruppa
        </button>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-900/50 text-red-300 text-sm border-b border-red-800">{error}</div>
      )}

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Filters sidebar */}
        <div className="w-56 shrink-0 overflow-y-auto border-r border-gray-800 p-3 space-y-4">
          <GraphFilters
            filters={filters}
            availableRelationTypes={availableRelationTypes}
            availableSourceTypes={availableSourceTypes}
            availableChannelTypes={availableChannelTypes}
            onChange={setFilters}
          />

          {/* Document selector */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Documenti</p>
            {contentItems.length === 0 ? (
              <p className="text-xs text-gray-600">Nessun documento pronto</p>
            ) : (
              <div className="space-y-1">
                {contentItems.map((item) => {
                  const selected = selectedDocIds.includes(item.id);
                  const name = item.title || item.id.slice(0, 8);
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleDoc(item.id)}
                      className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded text-xs transition-colors ${
                        selected ? 'bg-blue-900/40 text-blue-300' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      {selected
                        ? <CheckSquare className="w-3.5 h-3.5 shrink-0" />
                        : <Square className="w-3.5 h-3.5 shrink-0" />
                      }
                      <FileText className="w-3 h-3 shrink-0 opacity-60" />
                      <span className="truncate">{name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {graphSlice && (
            <div className="p-3 bg-gray-800 rounded-lg text-xs text-gray-400 space-y-0.5">
              <div>{graphSlice.stats.entityCount} entità</div>
              <div>{graphSlice.stats.relationCount} relazioni</div>
              <div>{graphSlice.stats.snippetCount} snippet</div>
              <div>{graphSlice.stats.documentCount} fonti</div>
            </div>
          )}
        </div>

        {/* Graph canvas */}
        <div className="flex-1 overflow-hidden p-3 relative">
          <GraphViewer
            entities={graphSlice?.entities ?? []}
            relations={graphSlice?.relations ?? []}
            filters={filters}
            groupByType={groupByType}
            onSelectEntity={handleEntityClick}
            onSelectRelation={handleRelationClick}
            loading={loading}
          />

          {/* Graph legend */}
          <div className="absolute bottom-5 right-5 bg-gray-900/80 border border-gray-700 rounded-lg p-3 text-xs pointer-events-none">
            <div className="font-semibold text-gray-400 mb-2 uppercase tracking-wide">Legenda</div>
            <div className="flex gap-4">
              <div>
                <div className="text-gray-500 mb-1">Entità</div>
                {LEGEND_NODES.map((n) => (
                  <div key={n.label} className="flex items-center gap-1.5 mb-0.5">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: n.color }} />
                    <span className="text-gray-300">{n.label}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-gray-500 mb-1">Livello</div>
                {LEGEND_LEVELS.map((l) => (
                  <div key={l.label} className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="w-5 h-0.5 shrink-0"
                      style={
                        l.dash
                          ? { backgroundImage: `repeating-linear-gradient(90deg,${l.color} 0,${l.color} 3px,transparent 3px,transparent 5px)` }
                          : { backgroundColor: l.color }
                      }
                    />
                    <span className="text-gray-300">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Side panel */}
        {panelMode !== 'none' && (
          <div className="w-80 shrink-0 border-l border-gray-800 overflow-hidden">
            {panelMode === 'entity' && (
              <EntityDetail
                entity={selectedEntity}
                relations={graphSlice?.relations ?? []}
                documents={graphSlice?.documents ?? []}
                onClose={() => setPanelMode('none')}
                onSelectRelation={(rel) => {
                  setSelectedRelation(rel);
                  setPanelMode('evidence');
                }}
              />
            )}
            {panelMode === 'evidence' && (
              <EvidencePanel
                relation={selectedRelation}
                snippets={graphSlice?.snippets ?? []}
                documents={graphSlice?.documents ?? []}
                onClose={() => setPanelMode('none')}
              />
            )}
          </div>
        )}
      </div>

      {/* Report drawer */}
      {reportBody && (
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gray-900 border-t border-gray-700 flex flex-col z-10">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
            <span className="text-sm font-semibold">Report generato</span>
            <button onClick={() => setReportBody(null)} className="text-gray-400 hover:text-white text-lg">×</button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 prose prose-invert prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-sm text-gray-200">{reportBody}</div>
          </div>
        </div>
      )}
    </div>
  );
}
